import { createContext } from "react";
import { DEFAULT_TIMINGS, Timings } from "../../config";

/**
 * Fallback audience, used when config.json omits `clientId`.
 *
 * Deliberately the historical value rather than an empty string: an existing
 * deployment whose config.json predates this key keeps working, instead of
 * failing login with a confusing "Invalid audience". New deployments should
 * still set it explicitly.
 */
export const DEFAULT_CLIENT_ID = "emms-portal";

/**
 * Everything read from `public/config.json` at startup. Nothing here is baked
 * into the bundle, so one build serves every environment — production mounts
 * its own config.json over the one in the image.
 *
 * See config/timings.ts for what each timing means.
 */
export interface Config extends Timings {
  /** Base URL of the EMMS portal API, e.g. https://emms.example.com/api/v1 */
  baseURL: string;
  /** ArcGIS map service URL, proxied through the portal server. */
  arcgisURL: string;
  /**
   * Auth server API BASE — origin plus its apiPrefix, e.g.
   * https://auth.example.com/api. NOT ".../api/auth", which is an endpoint;
   * that mistake shows up as "405 Method Not Allowed" on login.
   */
  authServer: string;
  /**
   * Audience this portal authenticates against. Must match the clientId the
   * portal is registered under on the auth server, and the portal server's
   * CLIENT_ID — the token's audience is what ties a session to this app's
   * session feed.
   */
  clientId: string;
  /** Hide bollard inventory/fault/maintenance screens for sites without them. */
  disableBollard?: boolean;
  /** Optional Lands Department basemap API key. */
  hkMapKey?: string;
  /**
   * Earliest month of data this deployment shows, as `YYYY-MM`. Bounds the
   * overview line charts and floors every month picker, so a site whose
   * records only start part-way through the system's life is not shown years
   * of empty chart. Omit to show all history.
   *
   * Replaced a hardcoded per-region table (`getMin`) that had drifted out of
   * step with the deployment it shipped in.
   */
  dataStartMonth?: string;
}

export const configContext = createContext<Config>({
  arcgisURL: "",
  baseURL: "",
  authServer: "",
  clientId: DEFAULT_CLIENT_ID,
  disableBollard: false,
  hkMapKey: "",
  dataStartMonth: undefined,
  ...DEFAULT_TIMINGS,
});
